import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Search } from 'lucide-react';
import { displayBankName } from '../../utils/displayBank';

export type MerchantBankOption = {
  id: string;
  label: string;
};

export function BankFilter({
  banks,
  selectedBanks,
  onChange,
  merchantBanks,
  selectedMerchantBanks,
  onMerchantBanksChange,
}: {
  banks: string[];
  selectedBanks: string[];
  onChange: (banks: string[]) => void;
  merchantBanks?: MerchantBankOption[];
  selectedMerchantBanks?: string[];
  onMerchantBanksChange?: (banks: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [panelBox, setPanelBox] = useState<{ top: number; left: number } | null>(null);

  const selected = useMemo(() => new Set(selectedBanks), [selectedBanks]);
  const merchantSelected = useMemo(
    () => new Set(selectedMerchantBanks ?? []),
    [selectedMerchantBanks],
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return banks;
    return banks.filter(
      (bank) =>
        bank.toLowerCase().includes(q) || displayBankName(bank).toLowerCase().includes(q),
    );
  }, [banks, query]);

  const hasMerchant = Boolean(merchantBanks && merchantBanks.length > 0 && onMerchantBanksChange);
  const panelWidth = hasMerchant ? 420 : 240;
  const merchantCount = selectedMerchantBanks?.length ?? 0;
  const merchantAll = hasMerchant && merchantCount === (merchantBanks?.length ?? 0);

  const buttonLabel =
    selectedBanks.length === 0 && merchantCount === 0
      ? 'Банк'
      : selectedBanks.length === banks.length && (!hasMerchant || merchantAll)
        ? 'Все банки'
        : `Банк · ${selectedBanks.length + (hasMerchant ? merchantCount : 0)}`;

  const syncPosition = useCallback(() => {
    const trigger = rootRef.current;
    if (!trigger || !open) return;
    const rect = trigger.getBoundingClientRect();
    const width = panelWidth;
    const pad = 8;
    const left = Math.min(Math.max(pad, rect.right - width), window.innerWidth - width - pad);
    const top = Math.min(rect.bottom + 6, window.innerHeight - pad);
    setPanelBox({ top, left });
  }, [open, panelWidth]);

  useLayoutEffect(() => {
    if (!open) return;
    syncPosition();
  }, [open, selectedBanks.length, merchantCount, syncPosition]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => syncPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, syncPosition]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (bank: string) => {
    if (selected.has(bank)) {
      onChange(selectedBanks.filter((item) => item !== bank));
      return;
    }
    onChange([...selectedBanks, bank]);
  };

  const toggleMerchant = (id: string) => {
    if (!onMerchantBanksChange || !selectedMerchantBanks) return;
    if (merchantSelected.has(id)) {
      onMerchantBanksChange(selectedMerchantBanks.filter((item) => item !== id));
      return;
    }
    onMerchantBanksChange([...selectedMerchantBanks, id]);
  };

  const highlight = selectedBanks.length > 0 || merchantCount > 0;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={`inline-flex h-6 items-center rounded-md border px-2 text-[11px] font-semibold transition ${
          highlight
            ? 'border-sky-300 bg-sky-50 text-sky-800'
            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
        }`}
      >
        {buttonLabel}
      </button>

      {open && panelBox
        ? createPortal(
            <div
              ref={panelRef}
              className="fixed z-[80] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
              style={{ top: panelBox.top, left: panelBox.left, width: panelWidth }}
            >
              <div className="border-b border-slate-100 p-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Поиск банка"
                    className="h-8 w-full rounded-lg border border-slate-200 bg-slate-50 pl-7 pr-2 text-xs text-slate-800 outline-none focus:border-sky-300 focus:bg-white"
                  />
                </div>
                <div className="mt-1.5 flex gap-1">
                  <button
                    type="button"
                    className="h-6 flex-1 rounded-md border border-slate-200 text-[10px] font-semibold text-slate-600 hover:bg-slate-50"
                    onClick={() => onChange([...banks])}
                  >
                    Все
                  </button>
                  <button
                    type="button"
                    className="h-6 flex-1 rounded-md border border-slate-200 text-[10px] font-semibold text-slate-600 hover:bg-slate-50"
                    onClick={() => onChange([])}
                  >
                    Сбросить
                  </button>
                </div>
              </div>
              <div className={hasMerchant ? 'grid grid-cols-2 divide-x divide-slate-100' : undefined}>
                <div className="vnd-scroll max-h-64 overflow-y-auto p-1.5">
                  {filtered.length === 0 ? (
                    <div className="px-2 py-4 text-center text-[11px] text-slate-400">Ничего не найдено</div>
                  ) : (
                    filtered.map((bank) => {
                      const checked = selected.has(bank);
                      return (
                        <button
                          key={bank}
                          type="button"
                          onClick={() => toggle(bank)}
                          className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition ${
                            checked ? 'bg-sky-50' : 'hover:bg-slate-50'
                          }`}
                        >
                          <CheckBox checked={checked} />
                          <span className="truncate text-[11px] font-semibold text-slate-800">
                            {displayBankName(bank)}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
                {hasMerchant ? (
                  <div className="p-1.5">
                    <div className="px-2 pb-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                      Оплата
                    </div>
                    {merchantBanks!.map((item) => {
                      const checked = merchantSelected.has(item.id);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => toggleMerchant(item.id)}
                          className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition ${
                            checked ? 'bg-sky-50' : 'hover:bg-slate-50'
                          }`}
                        >
                          <CheckBox checked={checked} />
                          <span className="truncate text-[11px] font-semibold text-slate-800">{item.label}</span>
                        </button>
                      );
                    })}
                    <div className="mt-1 flex gap-1 px-0.5">
                      <button
                        type="button"
                        className="h-6 flex-1 rounded-md border border-slate-200 text-[10px] font-semibold text-slate-600 hover:bg-slate-50"
                        onClick={() => onMerchantBanksChange?.(merchantBanks!.map((item) => item.id))}
                      >
                        Все
                      </button>
                      <button
                        type="button"
                        className="h-6 flex-1 rounded-md border border-slate-200 text-[10px] font-semibold text-slate-600 hover:bg-slate-50"
                        onClick={() => onMerchantBanksChange?.([])}
                      >
                        Сбросить
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function CheckBox({ checked }: { checked: boolean }) {
  return (
    <span
      className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
        checked ? 'border-sky-500 bg-sky-500 text-white' : 'border-slate-300 bg-white text-transparent'
      }`}
    >
      <Check className="h-2.5 w-2.5" strokeWidth={3} />
    </span>
  );
}
